import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createModifierApi,
  deleteModifierApi,
  getModifierApi,
  getModifiersApi,
  updateModifierApi,
} from "../api/modifiersApi";
import {
  catalogQueryKey,
  type CatalogScope,
} from "../api/catalogScope";
import type { ModifierId } from "@KassaroAB/shared/types/ids";
import type {
  RestCreateModifier,
  RestUpdateModifier,
} from "@KassaroAB/shared/types/modifier";

const modifiersQueryKey = (scope: CatalogScope | null) =>
  catalogQueryKey(scope, "modifiers");

const invalidateModifierQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  scope: CatalogScope | null,
) => {
  queryClient.invalidateQueries({ queryKey: modifiersQueryKey(scope) });
};

export const useGetModifiersQuery = (scope: CatalogScope | null) => {
  return useQuery({
    queryKey: modifiersQueryKey(scope),
    queryFn: () => getModifiersApi(scope!),
    enabled: !!scope,
  });
};

export const useGetModifierQuery = (
  scope: CatalogScope | null,
  modifierId: ModifierId | null | undefined,
) => {
  return useQuery({
    queryKey: [...modifiersQueryKey(scope), modifierId],
    queryFn: () => getModifierApi(scope!, modifierId!),
    enabled: !!scope && !!modifierId,
  });
};

export const useCreateModifierMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (modifier: RestCreateModifier) =>
      createModifierApi(scope!, modifier),
    onSuccess: () => {
      invalidateModifierQueries(queryClient, scope);
    },
  });
};

export const useUpdateModifierMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      modifierId,
      modifier,
    }: {
      modifierId: ModifierId;
      modifier: RestUpdateModifier;
    }) => updateModifierApi(scope!, modifierId, modifier),
    onSuccess: () => {
      invalidateModifierQueries(queryClient, scope);
    },
  });
};

export const useDeleteModifierMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (modifierId: ModifierId) =>
      deleteModifierApi(scope!, modifierId),
    onSuccess: () => {
      invalidateModifierQueries(queryClient, scope);
    },
  });
};
